<template>
  <div class="page-wrapper">
    <WaveBg />

    <div class="content" v-if="sectorList.length > 0">
      <div class="location-label">{{ currentLearningType.name }}</div>
      <header class="sector-list-title">SELECT YOUR SECTOR</header>
      <section class="sector-list">
        <article
          class="sector-card"
          v-for="sector in sectorList"
          :key="sector.id"
          @click="goThemeList(sector)"
          @keypress.enter="goThemeList(sector)"
        >
          <div
            class="image-box"
            :style="`background-image: url(${$path.resolveImage(
              sector.bg_image,
              $assets.image.default['hub_default_bg.jpg']
            )});`"
          >
            <div class="icon-box">
              <inline-svg :src="$path.resolveImage(sector.image)" fill="#6d6e71" width="45%" height="70%" />
            </div>
          </div>
          <div class="text-box">
            <div class="sector-name">{{ sector.name }}</div>
            <div class="sector-summary">{{ sector.summary }}</div>
          </div>
        </article>
      </section>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useRouter, onBeforeRouteUpdate } from 'vue-router';

import $assets from '@/assets';
import WaveBg from '@/components/common/WaveBackground.vue';
import LearningTypeService from '@/services/learning-type';
import SectorService from '@/services/sector';
import useSessionStore from '@/stores/session';
import $path from '@/utils/path';

const props = defineProps({
  learningTypeId: {
    type: String,
    required: true,
  },
});

const $router = useRouter();
const SessionStore = useSessionStore();
const sectorList = ref([]);

const currentLearningType = computed(() => {
  return SessionStore.HUBSettings.currentLearningType;
});

onBeforeRouteUpdate(async (to) => {
  // Parameter [learningTypeId] has changed
  const { learningTypeId } = to.params;
  const learningTypeResult = await LearningTypeService.getLearningType(learningTypeId);
  const learningType = learningTypeResult.record;

  if (!learningType || learningType.learning_type_status !== 'available' || !learningType.is_ui_gate) {
    $router.push({ name: 'dashboard' });
  }

  const currentFunction = {
    key: learningType.learning_type_key,
    name: learningType.name,
  };

  SessionStore.setHUBSettingItems({ currentFunction, currentLearningType: learningType });
});

async function getSectorList() {
  const sectorResults = await SectorService.getSectors(props.learningTypeId);
  sectorList.value = sectorResults.records;
}

function goThemeList(sector) {
  $router.push({
    name: 'themeList',
    params: { sectorId: sector.id },
  });
}

watch(
  () => props.learningTypeId,
  () => {
    getSectorList();
  },
  { immediate: true }
);
</script>

<style scoped>
.page-wrapper {
  width: 100%;
  height: 100%;
}

.content {
  display: flex;
  flex-direction: column;
  align-items: center;

  position: relative;
  box-sizing: border-box;
  min-height: 100%;
  padding: 3em 0;
  font-weight: 700;
}
.content .location-label {
  font-size: 1.2em;
  color: #fff;
  text-align: center;
}
.content .sector-list-title {
  margin: 0.5em 0 2em;
  font-size: 1.5em;
  color: #fff;
  text-align: center;
}

.content .sector-list {
  display: grid;
  justify-content: center;
  grid-template-columns: repeat(auto-fit, 85%);
  row-gap: 3em;
}

.sector-list > .sector-card {
  border-radius: 0.8em;
  background-color: #fff;
  cursor: pointer;
  overflow: hidden;
  transition: box-shadow 0.3s;
}
.sector-list > .sector-card:hover {
  box-shadow: 0 0 0.8em 0.1em rgba(000, 000, 000, 0.8);
}

.sector-card .image-box {
  display: flex;
  justify-content: center;
  align-items: flex-end;
  height: 10em;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
}
.sector-card .image-box .icon-box {
  display: flex;
  justify-content: center;
  align-items: flex-end;
  width: 8em;
  height: 4em;
  border-radius: 4em 4em 0 0;
  background-color: #fff;
}

.sector-card .text-box {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  min-height: 12em;
  box-sizing: border-box;
  padding: 0 1em;
  color: #808285;
}
.sector-card .text-box .sector-name {
  margin: 1em 0;
  font-size: 1.5em;
  text-align: center;
}
.sector-card .text-box .sector-summary {
  font-size: 1.1em;
  font-weight: 400;
}

/** Landscape mobile */
@media only screen and (orientation: landscape) and (max-height: 600px) {
  .content .sector-list {
    grid-auto-flow: column;
    grid-template-columns: repeat(auto-fit, 17em);
    column-gap: 1em;
  }
}

/** Larger devices */
@media only screen and (min-width: 601px) and (min-height: 601px) {
  .content {
    justify-content: center;
  }
  .content .sector-list {
    grid-auto-flow: column;
    grid-template-columns: repeat(auto-fit, 18em);
    column-gap: 3em;
  }
}
</style>
